import type { Account } from '@api/hooks/useAccounts';
import { useAccounts } from '@api/hooks/useAccounts';
import { useAccountDetails } from '@api/hooks/useAccounts';
import { DateRangeFormPickerWrapper } from '@components/CalendarDateRange';
import {
	Button,
	Loader,
	RadioButton,
	RadioGroup,
	SelectInput,
	Typography,
} from '@gurupay/gp-ui-library';
import type { CreateStatementParams } from '@hooks/useRequestStatement';
import { useRequestStatement } from '@hooks/useRequestStatement';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const AVAILABLE_FILE_FORMATS = [
	{ label: 'PDF', value: 'pdf' },
	{ label: 'CSV', value: 'csv' },
	{ label: 'XML', value: 'xml' },
	{ label: 'XLSX', value: 'xlsx' },
];

type FileTypeProps = 'pdf' | 'csv' | 'xml' | 'xlsx';
type LogicTypeProps = 'available_balance' | 'balance';

const GenerateStatementsView = () => {
	const [ibanSearchParam, setIbanSearchParam] = useState('');
	const { t } = useTranslation('', { keyPrefix: 'statements' });
	const [search] = useSearchParams();
	const navigate = useNavigate();
	const paramId = search.get('uuid');

	const BALANCE_TYPES = useMemo(
		() => [
			{ label: t('balanceTypes.balance'), value: 'balance' },
			{
				label: t('balanceTypes.availableBalance'),
				secondaryText: t('balanceTypes.availableBalanceSecondary'),
				value: 'available_balance',
			},
		],
		[t],
	);

	const { data: accounts, isLoading: areIbansLoading } = useAccounts({
		limit: 100,
		iban: ibanSearchParam,
	});

	const { data: accountData, isLoading: isAccountLoading } = useAccountDetails(
		paramId ?? '',
	);

	const ibanArray =
		accounts?.items?.map((acc: Account) => ({
			label: acc.iban,
			value: acc.uuid,
		})) ?? [];

	const { control, setValue, handleSubmit, formState } = useForm<{
		type: FileTypeProps;
		accountUuid: string;
		dateTo?: string;
		dateFrom?: string;
		balanceType: LogicTypeProps;
	}>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			type: 'pdf',
			balanceType: 'balance',
		},
	});

	const { mutate, isPending } = useRequestStatement();

	const onSubmit = (data: CreateStatementParams) => {
		mutate(data, {
			onSuccess: () => {
				navigate('/statements/download');
			},
		});
	};

	useEffect(() => {
		if (!accountData?.uuid) {
			return;
		}

		setValue('accountUuid', accountData.uuid, { shouldValidate: true });
	}, [paramId, accountData?.uuid, setValue]);

	const prefilledValue = accountData?.uuid
		? {
				label: accountData.iban,
				value: accountData.uuid,
			}
		: undefined;

	return (
		<form
			className="mt-space-24 sm:mt-space-40 flex flex-col gap-space-16 max-w-[500px] mx-auto"
			onSubmit={handleSubmit(onSubmit)}
		>
			<DateRangeFormPickerWrapper
				control={control}
				disabledFuture
				error={formState.errors.dateFrom || formState.errors.dateTo}
				required
			/>

			<Controller
				control={control}
				name="accountUuid"
				render={({ field }) => {
					return (
						<SelectInput
							{...field}
							error={formState.errors.accountUuid?.message}
							isLoading={areIbansLoading || isAccountLoading}
							isRequired
							label={t('accounts')}
							onSearch={setIbanSearchParam}
							options={ibanArray}
							selectedItem={prefilledValue}
							showClear
						/>
					);
				}}
				rules={{ required: t('required') }}
			/>

			<Typography level="h3">{t('fileFormat')}</Typography>
			<Controller
				control={control}
				name="type"
				render={({ field }) => (
					<RadioGroup className="flex gap-space-32 flex-col sm:flex-row" {...field}>
						{AVAILABLE_FILE_FORMATS.map((item) => (
							<RadioButton key={item.value} {...item} />
						))}
					</RadioGroup>
				)}
			/>

			<Typography level="h3">{t('statementLogic')}</Typography>
			<Controller
				control={control}
				name="balanceType"
				render={({ field }) => (
					<RadioGroup className="flex gap-space-32 flex-col sm:flex-row" {...field}>
						{BALANCE_TYPES.map((item) => (
							<RadioButton key={item.value} {...item} />
						))}
					</RadioGroup>
				)}
			/>

			<Button disabled={isPending || !formState.isValid} type="submit">
				{isPending && <Loader />}
				{t('generateBtn')}
			</Button>
		</form>
	);
};

export default GenerateStatementsView;
